/**
 * Two-Categorical CSK demo navigation registry.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { defineMonographNavigation } from "@csk/demo-ui/components/monograph-demo-shell";
import { DEMO_COLOR } from "@csk/demo-ui/design-system";
import { OverviewPanel } from "../panels/OverviewPanel.js";
import { SdpAlphabetPanel } from "../panels/SdpAlphabetPanel.js";
import { GradeMonoidPanel } from "../panels/GradeMonoidPanel.js";
import { BridgeConstructorPanel } from "../panels/BridgeConstructorPanel.js";
import { SnfWalkerPanel } from "../panels/SnfWalkerPanel.js";
import { SquareStitcherPanel } from "../panels/SquareStitcherPanel.js";
import { NaturalitySquaresGamePanel } from "../panels/NaturalitySquaresGamePanel.js";
import { CompositionPanel } from "../panels/CompositionPanel.js";
import { AttackSimulatorPanel } from "../panels/AttackSimulatorPanel.js";
import { TwoCellTransformationsPanel } from "../panels/TwoCellTransformationsPanel.js";
import { TheoremsPanel } from "../panels/TheoremsPanel.js";
import { LimitsPanel } from "../panels/LimitsPanel.js";
import { ManuscriptPanel } from "../panels/ManuscriptPanel.js";

export const NAV = [
  {
    id: "overview",
    label: "The Thesis",
    subtitle: "Four incidents · one structural fix",
    icon: "◇",
    accent: DEMO_COLOR.cyan,
    component: OverviewPanel,
  },
  {
    id: "sdp-alphabet",
    label: "Seven SDPs",
    subtitle: "The typing-kit alphabet",
    icon: "Σ",
    accent: DEMO_COLOR.pink,
    component: SdpAlphabetPanel,
  },
  {
    id: "grade-monoid",
    label: "Grade Monoid",
    subtitle: "G = ⊕_s ℤ/2[Φ(s)] · ICG envelopes",
    icon: "⊕",
    accent: DEMO_COLOR.violet,
    component: GradeMonoidPanel,
  },
  {
    id: "bridge-constructor",
    label: "Bridge Constructor",
    subtitle: "Live typed-admission playground",
    icon: "⟶",
    accent: DEMO_COLOR.cyan,
    component: BridgeConstructorPanel,
  },
  {
    id: "snf-walker",
    label: "SNF Walker",
    subtitle: "B1 → B2 → B3 → B4 · T4 diagonality",
    icon: "▦",
    accent: DEMO_COLOR.gold,
    component: SnfWalkerPanel,
  },
  {
    id: "square-stitcher",
    label: "Square Stitcher",
    subtitle: "Mini-game · rotate 2-cells before stitching",
    icon: "▦",
    accent: DEMO_COLOR.cyan,
    component: SquareStitcherPanel,
  },
  {
    id: "naturality-squares",
    label: "Naturality Squares",
    subtitle:
      "Mini-game · complete the commuting square; non-natural 2-cells have no slot",
    icon: "⊞",
    accent: DEMO_COLOR.violet,
    component: NaturalitySquaresGamePanel,
  },
  {
    id: "composition",
    label: "Composition",
    subtitle: "Case A · B · C · D · ICG detection",
    icon: "∘",
    accent: DEMO_COLOR.indigo,
    component: CompositionPanel,
  },
  {
    id: "attack-simulator",
    label: "Attack Simulator",
    subtitle: "CD-1..CD-4 · binary admit vs graded refuse",
    icon: "✦",
    accent: DEMO_COLOR.danger,
    component: AttackSimulatorPanel,
  },
  {
    id: "two-cell-transformations",
    label: "2-Cell Transformations",
    subtitle: "Before/after diff · drawer context · SDP retyping",
    icon: "⇄",
    accent: DEMO_COLOR.cyan,
    component: TwoCellTransformationsPanel,
  },
  {
    id: "theorems",
    label: "Theorems T1–T4",
    subtitle: "Statements · operational · adversarial",
    icon: "⊢",
    accent: DEMO_COLOR.teal,
    component: TheoremsPanel,
  },
  {
    id: "limits",
    label: "Limits & Scope",
    subtitle: "Honest limits · trusted base · stack position",
    icon: "△",
    accent: DEMO_COLOR.mutedCool,
    component: LimitsPanel,
  },
  {
    id: "manuscript",
    label: "Manuscript",
    subtitle: "Chapter-by-chapter companion",
    icon: "✎",
    accent: DEMO_COLOR.gold,
    component: ManuscriptPanel,
  },
] as const;

export const NAVIGATION = defineMonographNavigation(NAV, {
  defaultPanelId: "overview",
});
